import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { mediaApi } from '../../utils/api';
import { imageRegex } from '../../utils/regex';
import { SaveError } from '../common/SaveError';
import { QcsButton } from '../common/basic/QcsButton';
import { LinearProgress as  QcsLinearProgress } from '@mui/material';

import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../common/basic/QcsModalBox';
import { QcsTypography } from '../common/basic/QcsTypography';
import { setErrorState } from '../../utils/error';
import { checkFileSize } from '../../utils/upload';
import { ErrorStateType } from '../../models/common';

interface Props {
  file: File | null;
  onFileUploaded: (
    url: string,
    id: string,
    filename?: string,
    mediaType?: string
  ) => void;
  onCloseOnError: () => void;
}

export const BannerInputUploadModal: FC<Props> = ({
  file,
  onFileUploaded,
  onCloseOnError,
}) => {
  const { t } = useTranslation();
  const [progressValue, setProgressValue] = useState(0);
  const [error, setError] = useState<ErrorStateType>();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);

  useEffect(() => {
    (async () => {
      setError('');
      setProgressValue(0);

      if (!file) {
        return;
      }

      try {
        if (!file.name.match(imageRegex)) {
          throw new Error();
        }

        checkFileSize(file);
        const res = await mediaApi.uploadMedia(identityCompanyId, false, file, {
          onUploadProgress: uploadProgress,
        });
        const path = res.data.path!;

        onFileUploaded(
          path,
          res.data.id!,
          res.data.fileName,
          res.data.mediaType
        );
      } catch (err) {
        setErrorState(err, setError, 'common.saveError');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const uploadProgress = (p: any) => {
    let newValue = 0;

    if (p.total && p.loaded) {
      newValue = Math.floor((p.loaded / p.total) * 100);
    }

    if (progressValue !== newValue) {
      setProgressValue(newValue);
    }
  };

  const handleOnClose = () => {
    if (!error) {
      return;
    }

    onCloseOnError();
  };

  return (
    <QcsModal open={!!file} onClose={handleOnClose}>
      <QcsModalBox>
        <QcsTypography variant="h6">{t('html.uploading')}</QcsTypography>
        {error ? (
          <>
            <SaveError error={error} />
            <QcsButton onClick={onCloseOnError}>{t('common.close')}</QcsButton>
          </>
        ) : (
          <QcsLinearProgress variant="determinate" value={progressValue} />
        )}
      </QcsModalBox>
    </QcsModal>
  );
};
